import { cn } from './cn';

/**
 * success / warning / danger are reserved for statistical outcomes and
 * destructive confirmation. Categories and states use neutral or accent.
 */
export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

const TONES: Record<Tone, string> = {
  neutral: 'border-border bg-surface-muted text-fg-muted',
  accent: 'border-accent/20 bg-accent-muted text-accent',
  success: 'border-success/25 bg-success-muted text-success-fg',
  warning: 'border-warning/30 bg-warning-muted text-warning-fg',
  danger: 'border-danger/25 bg-danger-muted text-danger-fg',
};

/** Solid color for small indicators (dots, bars) in each tone. */
export const TONE_SOLID: Record<Tone, string> = {
  neutral: 'bg-fg-subtle',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

/** Readable text color for each tone on a neutral surface. */
export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-fg',
  accent: 'text-accent',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
};

export interface BadgeStyleOptions {
  tone?: Tone;
  size?: 'sm' | 'md';
  /** Hover and pointer affordance for a badge rendered as a `<button>`. */
  interactive?: boolean;
  className?: string;
}

/** Badge classes, e.g. for a `<button>` that opens a menu from a badge. */
export const badgeStyles = ({
  tone = 'neutral',
  size = 'sm',
  interactive = false,
  className,
}: BadgeStyleOptions = {}): string =>
  cn(
    'inline-flex max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-full border font-medium leading-none [&_svg]:shrink-0',
    size === 'sm'
      ? 'h-5 px-2 text-xs [&_svg]:size-3'
      : 'h-6 px-2.5 text-xs [&_svg]:size-3.5',
    TONES[tone],
    interactive &&
      'cursor-pointer select-none transition-[filter] duration-150 hover:brightness-[0.96] dark:hover:brightness-125',
    className
  );
